import {
  Home,
  Award,
  ChevronLeft,
  ChevronRight,
  Gift,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { APP_VERSION } from "@/version";
import assetsForApp from "../../appsAssetListing";
import { observer } from "mobx-react";
import { PRIVACY_POLICY_URL, TERMS_URL } from "@/utils/legalLinks";
import { cn } from "@/lib/utils";
import { BrandLogo } from "@/components/redesign/BrandLogo";
import navHome from "@/assets/images/navigation/nav-home.png";
import navPerks from "@/assets/images/navigation/nav-perks.png";
import navResults from "@/assets/images/navigation/nav-results.png";

export interface SidebarProps {
  activeItem?: "home" | "perks" | "results" | "pastExams" | "profile" | "changeExam";
  forceExpanded?: boolean;
}

const getActiveStyles = (isActive: boolean, mainColor: string) => ({
  container: "rounded-xl",
  containerStyle: isActive
    ? { backgroundColor: `${mainColor}1A` }
    : {},
  icon: isActive ? "" : "text-[#9a9a9a]",
  iconStyle: isActive ? { color: mainColor } : {},
  text: isActive ? "" : "text-[#9a9a9a]",
  textStyle: isActive ? { color: mainColor } : {},
});

interface NavItemProps {
  icon: LucideIcon;
  iconSrc?: string;
  label: string;
  active: boolean;
  expanded: boolean;
  mainColor: string;
  onClick: () => void;
  labelClassName?: string;
  primary?: boolean;
}

const NavItem = ({
  icon: Icon,
  iconSrc,
  label,
  active,
  expanded,
  mainColor,
  onClick,
  labelClassName,
  primary = false,
}: NavItemProps) => {
  const styles = getActiveStyles(active, mainColor);

  return (
    <button
      onClick={onClick}
      aria-label={label}
      className={cn(
        styles.container,
        "flex items-center",
        expanded
          ? "h-12 p-3 justify-start gap-3"
          : "h-11 w-11 justify-center p-3 lg:h-12 lg:w-auto lg:justify-start lg:gap-3"
      )}
      style={styles.containerStyle}
    >
      {iconSrc ? (
        <img src={iconSrc} alt="" className="h-5 w-5 shrink-0 object-contain" />
      ) : (
        <Icon
          className={cn("w-6 h-6 shrink-0", styles.icon)}
          style={styles.iconStyle}
        />
      )}
      <span
        className={cn(
          "text-sm font-inter tracking-tight whitespace-nowrap",
          primary ? (active ? "font-semibold" : "font-medium") : "font-semibold",
          styles.text,
          expanded ? "inline" : "hidden lg:inline",
          labelClassName
        )}
        style={styles.textStyle}
      >
        {label}
      </span>
    </button>
  );
};

const Sidebar = observer(
  ({ activeItem = "pastExams", forceExpanded = false }: SidebarProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const mainColor = assetsForApp.global.mainColor;
    const [isExpanded, setIsExpanded] = useState(false);
    const expanded = forceExpanded || isExpanded;
    const primaryActiveItem = activeItem === "results" || activeItem === "pastExams"
      ? "results"
      : activeItem === "perks"
        ? "perks"
        : activeItem === "home"
          ? "home"
          : null;

    return (
      <div
        className={cn(
          "relative h-full shrink-0 flex-col justify-between border-r border-[#dddfe4] bg-white pb-4 transition-[width,flex-basis] duration-200 ease-out",
          forceExpanded
            ? "flex w-full px-6"
            : expanded
              ? "hidden md:flex md:w-[240px] md:basis-[240px] md:px-0"
              : "hidden md:flex md:w-[76px] md:basis-[76px] md:px-2 lg:w-[240px] lg:basis-[240px] lg:px-0"
        )}
      >
        {!forceExpanded && (
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={expanded ? t("nav.collapse_sidebar") : t("nav.expand_sidebar")}
            className="absolute right-[-17px] top-[76px] z-30 hidden h-9 w-9 items-center justify-center rounded-full border border-[#dddfe4] bg-white text-[#5b616d] shadow-sm transition-colors hover:bg-[#f4f4f6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dp-brand-button)] focus-visible:ring-offset-2 md:flex lg:hidden"
            onClick={() => setIsExpanded((current) => !current)}
          >
            {expanded ? (
              <ChevronLeft aria-hidden="true" className="h-5 w-5" />
            ) : (
              <ChevronRight aria-hidden="true" className="h-5 w-5" />
            )}
          </button>
        )}
        <div className="flex flex-col">
          <div className="flex h-[72px] items-center justify-center border-b border-[#dddfe4] px-6 lg:justify-start">
            <BrandLogo className={cn(expanded ? "flex" : "hidden lg:flex")} compact />
            {!expanded && (
              <BrandLogo
                className="flex lg:hidden"
                compact
                iconOnly
              />
            )}
          </div>
          <div
            className={cn(
              "flex flex-col gap-3 p-6 md:px-0 lg:px-6",
              expanded ? "items-stretch" : "items-center lg:items-stretch"
            )}
          >
            <NavItem
              icon={Home}
              iconSrc={navHome}
              label={t("nav.home")}
              active={primaryActiveItem === "home"}
              expanded={expanded}
              mainColor={mainColor}
              onClick={() => navigate("/study")}
              primary
            />
            <NavItem
              icon={Gift}
              iconSrc={navPerks}
              label={t("nav.perks", "Perks")}
              active={primaryActiveItem === "perks"}
              expanded={expanded}
              mainColor={mainColor}
              onClick={() => navigate("/perks")}
              primary
            />
            <NavItem
              icon={Award}
              iconSrc={navResults}
              label={t("nav.results")}
              active={primaryActiveItem === "results"}
              expanded={expanded}
              mainColor={mainColor}
              onClick={() => navigate("/results")}
              primary
            />
          </div>
        </div>
        <div
          className={cn(
            "w-full flex-col items-center gap-2 text-xs text-[#9a9a9a]",
            expanded ? "flex" : "hidden lg:flex"
          )}
        >
          <div className="flex items-center gap-2">
            <a
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#5f5f5f] hover:underline"
            >
              {t("common.privacy_policy")}
            </a>
            <span aria-hidden="true">|</span>
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#5f5f5f] hover:underline"
            >
              {t("common.terms")}
            </a>
          </div>
          <p>v{APP_VERSION}</p>
        </div>
      </div>
    );
  }
);

export default Sidebar;
